import classNames from 'classnames';
import { get, includes, isEmpty, orderBy, toLower } from 'lodash-es';
import ReactMarkdown from 'react-markdown';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  GroupBox,
  ScrollView,
  Select,
  Tab,
  TabBody,
  Tabs,
  TextInput,
  Tooltip,
  Window,
  WindowContent,
  WindowHeader
} from 'react95';
import { userSelector } from '../../selectors/authSelectors';
import {
  formatActivityLog,
  formatAskLog,
  formatComments,
  formatUserEscLog,
  highlightText,
  prepareUserDropdownList
} from '../../utils/taskUtils';
import './TaskActivity.scss';
import {
  useGetTaskActivitiesQuery,
  useGetTaskCommentsQuery,
  useGetTaskQuery,
  useGetAskLogQuery,
  useGetUserEscalationErrorsQuery,
  useSendInternalCommentMutation
} from '../../api/opsSystemApi';
import { useParams } from 'react-router-dom';
import { useQDeckUserEmailNameMap } from '../../api/qdeckApi';
import { Box } from '@mui/material';

/**
 * @description Polling interval in milliseconds for LLM review status updates
 */
export const LLM_REVIEW_POLLING_INTERVAL_MS = 10000;

const TaskActivity = () => {
  const user = useSelector(userSelector);

  const { taskId } = useParams();
  const [pollingInterval, setPollingInterval] = useState(0);

  const { data: task = {}, isLoading: isTaskLoading } = useGetTaskQuery(taskId, { pollingInterval });
  const { data: userEscLog = [], isLoading: isUserEscLogLoading } = useGetUserEscalationErrorsQuery(taskId);
  const { data: comments = [], isLoading: isCommentsLoading } = useGetTaskCommentsQuery(taskId, { pollingInterval });
  const { data: activityLog = [], isLoading: isActivityLogLoading } = useGetTaskActivitiesQuery(taskId, { pollingInterval });
  const { data: askLog = [], isLoading: isAskLogLoading } = useGetAskLogQuery(taskId);

  const [activeTab, setActiveTab] = useState(0);
  const [commentBody, setCommentBody] = useState('');
  const [commentTags, setCommentTags] = useState([]);
  const [showTagList, setShowTagList] = useState(false);
  const [messageSearch, setMessageSearch] = useState('');
  const [filteredComments, setFilteredComments] = useState([]);

  const [sendInternalComment] = useSendInternalCommentMutation();

  const { qdeckUserEmailNameMap, isLoading: isQDeckUserEmailNameMapLoading } = useQDeckUserEmailNameMap();

  const llmReviewInProgress = get(task, ['ops_status', 'llmReviewInProgress'], false);

  // Update polling interval when LLM review status changes
  useEffect(() => {
    setPollingInterval(llmReviewInProgress ? LLM_REVIEW_POLLING_INTERVAL_MS : 0);
  }, [llmReviewInProgress]);

  const commentInputRef = useRef(null);
  const tagListRef = useRef(null);

  const formattedActivityLog = useMemo(() => {
    if (activityLog) {
      return formatActivityLog(activityLog, qdeckUserEmailNameMap);
    }
    return [];
  }, [activityLog, qdeckUserEmailNameMap]);

  const formattedAskLog = useMemo(() => {
    if (askLog) {
      return formatAskLog(askLog, qdeckUserEmailNameMap);
    }
    return [];
  }, [askLog, qdeckUserEmailNameMap]);

  useEffect(() => {
    const focusElement = showTagList ? tagListRef.current : commentInputRef.current;
    const blurElement = showTagList ? commentInputRef.current : tagListRef.current;

    if (focusElement && focusElement !== document.activeElement) {
      focusElement.focus();
    }

    if (blurElement && blurElement === document.activeElement) {
      blurElement.blur();
    }
  }, [showTagList]);

  useEffect(() => {
    if (commentBody) {
      // If a comment tag is deleted, remove email from array so they won't be notified
      const updatedTags = commentTags.filter((email) => {
        const tagName = '@' + qdeckUserEmailNameMap[email];
        return commentBody.includes(tagName);
      });
      if (updatedTags.length !== commentTags.length) {
        setCommentTags(updatedTags);
      }
    }
  }, [commentBody, commentTags, qdeckUserEmailNameMap]);

  const formattedComments = useMemo(() => {
    if (!isEmpty(comments)) {
      return formatComments(comments, qdeckUserEmailNameMap);
    }
    return [];
  }, [comments, qdeckUserEmailNameMap]);

  useEffect(() => {
    if (!isEmpty(formattedComments)) {
      setFilteredComments(
        formattedComments.filter((comment) => includes(toLower(comment.commentBody), toLower(messageSearch)))
      );
    }
  }, [messageSearch, formattedComments]);

  const formattedUserEscLog = useMemo(() => {
    if (userEscLog) {
      return formatUserEscLog(userEscLog, qdeckUserEmailNameMap);
    }
    return [];
  }, [userEscLog, qdeckUserEmailNameMap]);

  if (
    isTaskLoading ||
    isUserEscLogLoading ||
    isCommentsLoading ||
    isActivityLogLoading ||
    isAskLogLoading ||
    isQDeckUserEmailNameMapLoading
  ) {
    return null;
  }

  const handleKeyPress = async (e) => {
    if (e.key === '@') {
      setCommentBody((commentBody || '') + '@');
      setShowTagList(true);
    }
  };

  const getUserDisplayName = (user) => {
    if (user.firstname && user.lastname) {
      return `${user.firstname} ${user.lastname}`;
    }
    return user.firstname || user.email;
  };

  /**
   * Sends a message that's visible only to internal users
   */
  const handleInternalCommentSubmit = async () => {
    if (!commentBody) {
      return;
    }

    await sendInternalComment({
      userId: task.user.id,
      taskId: task.id,
      authorEmail: user.email,
      authorName: getUserDisplayName(user),
      commentBody,
      commentTags
    });

    setCommentBody('');
    setCommentTags([]);
  };

  return (
    <Window className='task-activity-window'>
      <div className='task-activity-window-inner'>
        <WindowHeader className='task-activity-header'>
          <div>Task Activity</div>
        </WindowHeader>
        <WindowContent className='task-activity-content'>
          <Tabs value={activeTab} onChange={(val) => setActiveTab(val)}>
            <Tab className={classNames('task-activity-tab', { selected: !activeTab })} value={0}>
              Comms
            </Tab>
            <Tab className={classNames('task-activity-tab', { selected: activeTab === 1 })} value={1}>
              Activity Log
            </Tab>
            <Tab className={classNames('task-activity-tab', { selected: activeTab === 2 })} value={2}>
              Ask Log
            </Tab>
            <Tab className={classNames('task-activity-tab', { selected: activeTab === 3 })} value={3}>
              User Escs
            </Tab>
          </Tabs>
          <TabBody>
            {!activeTab && (
              <ScrollView className='task-activity-container'>
                <TextInput
                  ref={commentInputRef}
                  className='task-activity-comment-input'
                  key='commentInput'
                  placeholder="Type '@' to tag someone. Click Internal Comment when you're ready to send a message :)"
                  value={commentBody}
                  onKeyDown={(e) => handleKeyPress(e)}
                  onChange={(e) => setCommentBody(e.target.value)}
                  multiline
                  rows={4}
                />
                <div className='task-activity-comment-actions'>
                  <Select
                    value=''
                    className={classNames('task-activity-comment-tag-list', { hidden: !showTagList })}
                    ref={tagListRef}
                    variant='flat'
                    open={showTagList}
                    onOpen={() => setShowTagList(true)}
                    onClose={() => setShowTagList(false)}
                    options={prepareUserDropdownList(qdeckUserEmailNameMap, (email) => !commentTags.includes(email))}
                    onChange={(opt) => {
                      if (opt.value) {
                        setCommentTags([...commentTags, opt.value]);
                        setCommentBody(commentBody + opt.label + ' ');
                      }
                    }}
                    width={250}
                    menuMaxHeight={300}
                  />
                  <Box marginLeft='auto'>
                    <Button
                      className='internal-comment-button'
                      onClick={handleInternalCommentSubmit}
                      primary
                      variant='flat'
                      size='md'>
                      <span>Internal Comment</span>
                    </Button>
                  </Box>
                </div>
                {formattedComments.length > 0 && (
                  <>
                    <div className='divider' />
                    <TextInput
                      value={messageSearch}
                      onChange={(e) => setMessageSearch(e.target.value)}
                      placeholder='Search messages by content...'
                      key='messageSearchInput'
                    />
                  </>
                )}
                {filteredComments.map((record, i) => (
                  <div
                    className={classNames('task-activity-comment-record', {
                      internal: record.commentType !== 'User Message'
                    })}
                    key={'comment_' + i}>
                    <div className='task-activity-comment-record-header'>
                      <div
                        className={classNames('task-activity-comment-author', {
                          self: record.authorEmail === user.email,
                          user: record.authorType === 'user'
                        })}>
                        {record.authorName}
                      </div>
                      <div className='task-activity-comment-timestamp'>{record.createdAt}</div>
                    </div>
                    <div className='task-activity-comment-record-body'>
                      <Tooltip
                        disableMouseListener={isEmpty(record.tagged)}
                        disableFocusListener={isEmpty(record.tagged)}
                        className='task-activity-comment-tags'
                        position='top'
                        enterDelay={0}
                        text={
                          isEmpty(record.tagged)
                            ? ''
                            : 'Tagged: ' +
                            record.tagged.map((email) => qdeckUserEmailNameMap[email] || email).join(', ')
                        }>
                        <div
                          className={classNames('task-activity-comment-text', {
                            tagged: !isEmpty(record.tagged)
                          })}>
                          {record.authorName === 'LLM Review' ? (
                            <ReactMarkdown>{record.commentBody}</ReactMarkdown>
                          ) : (
                            highlightText(record.commentBody, messageSearch)
                          )}
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </ScrollView>
            )}
            {activeTab === 1 && (
              <ScrollView className='task-activity-container'>
                {Object.keys(formattedActivityLog).map((date) => (
                  <div key={date}>
                    <div className='task-activity-log-date'>{date}</div>
                    {formattedActivityLog[date].map((record, i) => (
                      <GroupBox className='task-activity-log-record' key={`${record.time}_${i}`} label={record.time}>
                        <div dangerouslySetInnerHTML={{ __html: record.activity }}></div>
                      </GroupBox>
                    ))}
                  </div>
                ))}
              </ScrollView>
            )}
            {activeTab === 2 && (
              <ScrollView className='task-activity-container'>
                {formattedAskLog.map((record, i) => (
                  <div className={classNames('task-activity-ask-log-record', record.className)} key={'ask_log_' + i}>
                    <div className='task-activity-ask-log-record-header'>
                      <div className='task-activity-ask-log-author'>
                        {(() => {
                          if (record.textType === 'user') {
                            return getUserDisplayName(task.user);
                          } else if (record.textType === 'manual') {
                            return record.bookkeeper;
                          } else {
                            return 'Automated Message';
                          }
                        })()}
                      </div>
                      <div className='task-activity-ask-log-timestamp'>{record.time}</div>
                    </div>
                    <div className='task-activity-ask-log-record-body'>
                      <div className='task-activity-ask-log-text'>{record.body}</div>
                    </div>
                  </div>
                ))}
              </ScrollView>
            )}
            {activeTab === 3 && (
              <ScrollView className='task-activity-container'>
                {orderBy(Object.keys(formattedUserEscLog), (date) => new Date(date), 'desc').map((date) => (
                  <div key={date}>
                    <div className='task-activity-log-date'>{date}</div>
                    {formattedUserEscLog[date].map((record, i) => (
                      <GroupBox className='task-activity-log-record' key={`${record.time}_${i}`} label={record.time}>
                        <p>
                          <b>Header:</b> {record.header}
                        </p>
                        <p>
                          <b>Body:</b> {record.body}
                        </p>
                        <p>
                          <b>Screen Shown:</b> {record.screenShown}
                        </p>
                        <p>
                          <b>Is Blocking:</b> {record.isBlocking ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <b>Is Post-Submit:</b> {record.isPostSubmit ? 'Yes' : 'No'}
                        </p>
                      </GroupBox>
                    ))}
                  </div>
                ))}
              </ScrollView>
            )}
          </TabBody>
        </WindowContent>
      </div>
    </Window>
  );
};

export default TaskActivity;
